/* eslint-disable react/no-array-index-key */
import { useState, useEffect } from 'react';
import { styled, css } from 'styled-components';
import { formatDistance, parseISO } from 'date-fns';
import { Tooltip } from '@hn-ui/tooltips';
import pluralize from 'pluralize';
import ProgressBar from './ProgressBar';
import { PieChart } from './PieChart';
import { Day as TestDay, AssignmentDay } from './Day';
import { StudentActivityStream } from './StudentActivityStream';
import { statusColorsMap, Grades, GradesWithGrade6 } from './utils';
import { CheckIcon } from '../Common/Icons';
import { TestContent } from './TestContent';
import {
  useWeeklyDashboardAnalytics,
  WeeklyDashboardEvents,
} from './hooks/useWeeklyDashboardAnalytics';

const NAME_MAX_LENGTH = 10;

export const Columns = css`
  display: grid;
  grid-template-columns: 1% 12% 21% 7% 8% 6.5% 6% 10% 11.8% 1%;
  column-gap: 1.7%;

  margin-bottom: 20px;
`;

const Grade = styled.section``;

const StudentStat = styled.div`
  ${Columns}

  position: relative;
`;

const Name = styled.a`
  display: inline-block;

  margin: 0;

  color: #42abe9;
  font-family: var(--main-font);
  font-style: normal;
  font-weight: bold;
  font-size: 16px;
  line-height: 24px;
  text-decoration: none;

  &:hover,
  &:focus {
    color: #1a95de;
  }
`;

const Heading = styled.h3`
  margin-top: 40px;
  margin-bottom: 34px;
  margin-left: 26px;

  color: #777b8b;
  font-family: var(--main-font);
  font-style: normal;
  font-weight: bold;
  font-size: 16px;
  line-height: 24px;
`;

export const DayRow = styled.div`
  display: flex;
  flex-direction: row;
`;

const StandardText = css`
  color: #242527;
  font-family: var(--main-font);
  font-size: 16px;
  line-height: 24px;
`;

const SmallText = css`
  color: #777b8b;
  font-family: var(--main-font);
  font-size: 13px;
  line-height: 16px;
`;

const Txt = styled.p`
  ${StandardText}
`;

const TxtInline = styled.p`
  ${StandardText}

  display: inline-block;
`;

const SubTxt = styled.p`
  ${SmallText}
`;

const StruggleCount = styled.sup`
  position: absolute;
  top: -15px;
  left: 17px;

  font-family: var(--main-font);
  font-style: normal;
  font-weight: bold;
  font-size: 13px;
  line-height: 20px;

  ${({ status }) =>
    status
      ? css`
          color: #de2d68;
        `
      : css`
          color: #9096aa;
          opacity: 0.7;
        `};
`;

const TooltipHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const TooltipHeading = styled.h1`
  display: flex;

  margin: 0;

  color: #242527;
  font-family: var(--main-font);
  font-style: normal;
  font-weight: bold;
  font-size: 16px;
  line-height: 24px;
`;

const TooltipStatus = styled.p`
  margin: 2px 0 0 0;

  color: ${({ status }) => (status ? '#DE2D68' : '#777B8B')};
  font-family: var(--main-font);
  font-weight: bold;
  font-size: 14px;
  line-height: 20px;
`;

const TooltipDescriptionContainer = styled.div`
  color: #242527;
  font-family: var(--main-font);
  font-size: 13px;
  line-height: 24px;
  letter-spacing: -0.2px;
  text-align: left;
`;

const TooltipDescriptionPart = styled.p`
  margin: 0;
`;

const TxtNoWrapNoMargin = styled.p`
  display: inline-block;

  margin: 0;

  ${StandardText}
  white-space: nowrap;
`;

const SetPieChart = styled(PieChart)`
  margin-right: 7px;
  margin-top: 2px;

  float: left;
`;

export const day = css`
  width: 24px;
  height: 24px;

  border-radius: 2px;
`;

const LastVisit = styled.p`
  ${SmallText}
  letter-spacing: -0.2px;

  ${({ isOnline }) =>
    isOnline &&
    css`
      color: #479a19;
    `}
`;

const EmptyDay = styled.div`
  ${day}
  margin-right: 6px;

  background: #e3f2fc;
`;

const ActiveDay = styled.p`
  ${day}
  ${StandardText}
  margin: 0 6px 0 0;

  text-align: center;

  background: #87c9f1;
`;

const PreLine = styled.div`
  white-space: pre-line;
`;

const TransparentBtn = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;

  padding: 8px 0 0 0;
  margin: 0;

  background: none;
  border: none;
  cursor: pointer;

  ${({ isActive }) =>
    !isActive &&
    css`
      cursor: not-allowed;
    `}

  ${({ isActive }) =>
    isActive &&
    `
    &:hover {
      & > svg > path {
        fill: #1A95DE;
      }
    }`}
`;

const ScCheckIcon = styled(CheckIcon)`
  & > path {
    fill: ${({ disabled }) => (disabled ? '#D7DDE4' : '#42ABE9')};
  }
  ${({ open }) => open && 'transform: rotate(180deg);'}
`;

const WarningIcon = styled.img`
  transform: translate(0px, 2px);
`;

const getNextGrade = (currentGrade) => {
  const index = Grades.indexOf(currentGrade);
  return GradesWithGrade6[index + 1];
};

const cutTxt = (str, max = 10) => {
  if (str.length > max) {
    return `${str.substring(0, max - 1)}...`;
  }
  return str;
};

const simplifyTime = (time, max = 99) => (time >= max ? '99+' : time);

const OnlineStatus = ({ lastVisit }) => {
  const currentDate = new Date();
  const visitDate = parseISO(lastVisit);
  const timeDiff = currentDate.getTime() - visitDate.getTime();
  const isOnline = timeDiff <= 300000;

  const status = isOnline
    ? 'online now'
    : formatDistance(visitDate, currentDate, { addSuffix: true });

  return <LastVisit isOnline={isOnline}>{status}</LastVisit>;
};

const StudentListItem = ({
  role,
  schoolId,
  student,
  days,
  isCurrentWeek,
  chosenDate,
  ISODate,
  toggleModal,
  isModalOpen,
  isWeekInThePast,
}) => {
  const {
    last_visit: lastVisit,
    not_seen_struggle_number: notSeenStruggleNumber,
    total_struggle_number: struggleCount,
    grade,
    course_completed: courseCompleted,
    next_grade_readiness: nextGradeReadiness,
  } = student;

  const [isActivityStream, setIsActivityStream] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [isCardsWasSeen, setIsCardsWasSeen] = useState(!notSeenStruggleNumber);

  const hasTouchedCards =
    student.activity_calendar.filter((ac) => ac.cards_touched > 0).length > 0;
  const hasAssignments = student.activity_calendar
    .map((ac) => ac.assignments)
    .some((a) => a.length > 0);
  const isActive = hasTouchedCards || hasAssignments;

  const arrowBtnTooltip = (() => {
    if (!isActive) {
      return 'No activity this week';
    }
    return isActivityStream ? 'Hide activity' : 'Show activity';
  })();

  useEffect(() => {
    if (clickCount === 2) {
      setIsCardsWasSeen(true);
    }
  }, [clickCount]);

  const sendGaEvent = useWeeklyDashboardAnalytics();

  const struggleIconWithTooltip = (
    <Tooltip
      content={{
        header: (
          <TooltipHeader>
            <TooltipHeading>{`${struggleCount} struggle ${pluralize(
              'task',
              struggleCount
            )}`}</TooltipHeading>
            <TooltipStatus status={!isCardsWasSeen}>
              {isCardsWasSeen ? 'viewed' : 'new'}
            </TooltipStatus>
          </TooltipHeader>
        ),
        body: (
          <TooltipDescriptionContainer>
            <TooltipDescriptionPart>
              {`${student.full_name} has ${struggleCount} struggle ${pluralize(
                'task',
                struggleCount
              )} this
        week. Click expand arrow on the right
        for details.`}
            </TooltipDescriptionPart>
          </TooltipDescriptionContainer>
        ),
      }}
      maxWidth='none'
    >
      <>
        <StruggleCount status={!isCardsWasSeen}>{struggleCount}</StruggleCount>
        <WarningIcon
          src={`https://static.happynumbers.com/teachers/reports/${
            isCardsWasSeen ? 'disabled-warning' : 'warning'
          }.svg`}
          onMouseEnter={() =>
            sendGaEvent(WeeklyDashboardEvents.struggleStudentIconHover)
          }
        />
      </>
    </Tooltip>
  );

  const studentNameWithTooltip = (
    <div>
      <Tooltip
        content={student.full_name}
        maxWidth='none'
        mouseOnly={true}
        interactive={student.full_name.length > NAME_MAX_LENGTH}
      >
        <Name
          href={student.link}
          onClick={() =>
            sendGaEvent(
              student.struggle
                ? WeeklyDashboardEvents.struggleStudentLinkClick
                : WeeklyDashboardEvents.studentLinkClick
            )
          }
          onMouseEnter={() =>
            sendGaEvent(WeeklyDashboardEvents.studentNameHover)
          }
          target='_blank'
        >
          {cutTxt(student.full_name, NAME_MAX_LENGTH)}
        </Name>
      </Tooltip>
      {isCurrentWeek &&
        (lastVisit ? (
          <OnlineStatus lastVisit={lastVisit} />
        ) : (
          <LastVisit>-</LastVisit>
        ))}
    </div>
  );

  const timeInfoWithTooltip = (
    <div>
      <Tooltip
        content={`Time Target on individual learning pathway (${student.weekly_target} min)`}
        maxWidth='none'
      >
        <div
          style={{ display: 'flex', width: 'fit-content' }}
          onMouseEnter={() =>
            sendGaEvent(WeeklyDashboardEvents.studentTimeTargetHover)
          }
        >
          <SetPieChart
            width={20}
            color={statusColorsMap.get(student.weekly_target_status)?.color}
            fill={statusColorsMap.get(student.weekly_target_status)?.fill}
          />
          <Txt>{student.total_time_spent}</Txt>
        </div>
      </Tooltip>
    </div>
  );

  const taskTargetInfoWithTooltip = (
    <Tooltip content={student.task_target_tooltip} maxWidth='none'>
      <ProgressBar
        percentage={student.weekly_plan}
        isTargetCompleted={student.weekly_plan === 100}
        onMouseEnter={() =>
          sendGaEvent(WeeklyDashboardEvents.studentTaskTargetHover)
        }
      />
    </Tooltip>
  );

  const taskCompletedInfoWithTooltip = (
    <div>
      <Tooltip
        content='Number of tasks solved by the student from individual pathway and assignments'
        maxWidth='264px'
      >
        <TxtInline
          onMouseEnter={() =>
            sendGaEvent(WeeklyDashboardEvents.studentTasksCompletedHover)
          }
        >
          {student.total_task_solved}
        </TxtInline>
      </Tooltip>
    </div>
  );

  const weeklyAccuracyInfoWithTooltip = (
    <div>
      <Tooltip
        content='Overall accuracy on tasks from individual learning pathway and assignments'
        maxWidth='254px'
      >
        <TxtInline>{`${student.accuracy}%`}</TxtInline>
      </Tooltip>
    </div>
  );

  const isTBD = grade === 'Grade level TBD';

  const studentNextGradeReadinessWithTooltip = isTBD ? (
    <Tooltip
      content='Data will be available after student completes the Placement test'
      maxWidth='320px'
    >
      <PreLine>
        <TxtNoWrapNoMargin>TBD</TxtNoWrapNoMargin>
        <SubTxt>-</SubTxt>
      </PreLine>
    </Tooltip>
  ) : (
    <Tooltip
      content={
        courseCompleted
          ? `${student.full_name} has reached Grade 6 readiness`
          : `${student.full_name} will be ready for ${getNextGrade(grade)}
        in ${pluralize('week', nextGradeReadiness, true)} assuming ${
              student.weekly_target
            } min/week
        practice`
      }
      maxWidth='none'
    >
      <PreLine
        onMouseEnter={() =>
          sendGaEvent(WeeklyDashboardEvents.studentNextGradeReadinessHover)
        }
      >
        <TxtNoWrapNoMargin>{`${pluralize(
          'week',
          nextGradeReadiness,
          true
        )}`}</TxtNoWrapNoMargin>
        <SubTxt>({student.weekly_target} min/week)</SubTxt>
      </PreLine>
    </Tooltip>
  );

  const numberOfVisibleChars = 13;
  const studentAreaOfFocusWithTooltip = (
    <div>
      {isTBD ? (
        <>
          <Tooltip
            content='Data will be available after student completes the Placement test'
            maxWidth='320px'
          >
            <TxtNoWrapNoMargin>TBD</TxtNoWrapNoMargin>
          </Tooltip>
          <SubTxt>-</SubTxt>
        </>
      ) : (
        <>
          <Tooltip
            content={
              student.area_of_focus.tag_name.length > numberOfVisibleChars
                ? student.area_of_focus.tag_name
                : ''
            }
            interactive={
              student.area_of_focus.tag_name.length > numberOfVisibleChars
            }
            maxWidth='none'
          >
            <TxtNoWrapNoMargin
              onMouseEnter={() =>
                sendGaEvent(
                  WeeklyDashboardEvents.studentCurrentAreaOfFocusHover
                )
              }
            >
              {cutTxt(student.area_of_focus.tag_name, numberOfVisibleChars)}
            </TxtNoWrapNoMargin>
          </Tooltip>
          <SubTxt>{student.area_of_focus.position_abbreviation}</SubTxt>
        </>
      )}
    </div>
  );

  const studentActivityStreamToggleArrowWithTooltip = (
    <Tooltip content={arrowBtnTooltip} mouseOnly={true} maxWidth='none'>
      <TransparentBtn
        type='button'
        onClick={() => {
          if (isActive) {
            setIsActivityStream(!isActivityStream);

            sendGaEvent(
              student.struggle
                ? WeeklyDashboardEvents.struggleStudentRowExpand
                : WeeklyDashboardEvents.studentRowExpand
            );
            setClickCount(clickCount + 1);
          }
        }}
        isActive={isActive}
      >
        <ScCheckIcon disabled={!isActive} open={isActivityStream} />
      </TransparentBtn>
    </Tooltip>
  );

  return (
    <>
      <StudentStat>
        {student.struggle ? struggleIconWithTooltip : <div />}

        {studentNameWithTooltip}

        <>
          <DayRow>
            {days.map(({ day: weekDay }, index) => {
              const activity = student.activity_calendar.find(
                (x) => x.date === weekDay
              );
              if (activity) {
                const hasTest = Boolean(
                  activity.assessments && activity.assessments.length > 0
                );
                const hasAssignmentTests = activity?.assignments?.length > 0;

                if (hasTest) {
                  const { type, status } =
                    activity.assessments[activity.assessments.length - 1];

                  return (
                    <TestDay
                      key={index}
                      isModalOpen={isModalOpen}
                      type={type}
                      status={status}
                      content={
                        <TestContent
                          activity={activity}
                          toggleModal={toggleModal}
                        />
                      }
                    />
                  );
                }

                if (hasAssignmentTests) {
                  const { assignments } = activity;
                  const type = 'assignment';

                  const assignmentsWithShortenNames = assignments.map(
                    (assignment) => ({
                      ...assignment,
                      name: cutTxt(assignment.name, 18),
                    })
                  );

                  return (
                    <AssignmentDay
                      key={activity.date}
                      type={type}
                      assignments={assignmentsWithShortenNames}
                      practiceTime={activity.time_spent}
                      isModalOpen={isModalOpen}
                    />
                  );
                }

                if (activity.time_spent > 0)
                  return (
                    <ActiveDay key={index}>
                      {simplifyTime(activity.time_spent)}
                    </ActiveDay>
                  );
              }

              return <EmptyDay key={index} />;
            })}
          </DayRow>

          {timeInfoWithTooltip}

          {taskTargetInfoWithTooltip}

          {taskCompletedInfoWithTooltip}

          {weeklyAccuracyInfoWithTooltip}

          {studentNextGradeReadinessWithTooltip}

          {studentAreaOfFocusWithTooltip}

          {studentActivityStreamToggleArrowWithTooltip}
        </>
      </StudentStat>

      {isActivityStream && (
        <StudentActivityStream
          role={role}
          schoolId={schoolId}
          studentId={student.id}
          chosenDate={chosenDate}
          isCurrentWeek={isCurrentWeek}
          ISODate={ISODate}
          isWeekInThePast={isWeekInThePast}
        />
      )}
    </>
  );
};

const StudentList = ({
  role,
  schoolId,
  heading,
  students,
  days,
  isCurrentWeek,
  chosenDate,
  ISODate,
  toggleModal,
  isModalOpen,
  isWeekInThePast,
}) => {
  const sortedStudents = students.sort((a, b) =>
    a.full_name.localeCompare(b.full_name)
  );

  return (
    <Grade>
      <Heading>{heading}</Heading>
      {sortedStudents.map((student) => (
        <StudentListItem
          key={`${student.id}_${ISODate}`}
          role={role}
          schoolId={schoolId}
          student={student}
          days={days}
          isCurrentWeek={isCurrentWeek}
          chosenDate={chosenDate}
          ISODate={ISODate}
          toggleModal={toggleModal}
          isModalOpen={isModalOpen}
          isWeekInThePast={isWeekInThePast}
        />
      ))}
    </Grade>
  );
};

export default StudentList;
